<template>
	<div class="flex flex-col h-screen max-h-screen min-h-screen overflow-hidden font-sans bg-white md:flex-row">
		<!-- Left: Main Image -->
		<div 
			class="relative flex items-center justify-center w-full h-full bg-gray-100 md:w-1/2"
			style="background-image: url('https://images.unsplash.com/photo-1464983953574-0892a716854b?auto=format&fit=crop&w=800&q=80'); background-size: cover; background-position: center; min-height: 400px;"
		>
			<div class="absolute p-2 bottom-8 right-8 bg-opacity-40">
				<button class="bg-[#961936] text-white font-bold px-6 py-3 shadow-lg text-lg tracking-wide hover:bg-[#a02a4a] transition" style="border-radius: 20px; font-family:'Nortune', sans-serif;">APPLY NOW</button>
			</div>
		</div>

		<!-- Right: Content -->
		<div class="md:w-1/2 w-full flex flex-col bg-[#961936] text-white relative h-full overflow-auto">
			<!-- Top: Title -->
			<div class="px-6 pt-6 pb-4">
				<div class="text-5xl font-extrabold leading-tight md:text-6xl drop-shadow-lg" style="text-shadow: 3px 3px 0 #00000055; font-family:'Nortune', sans-serif;">
					<span v-if="heroPrimaryLines.length" class="block leading-[0.85]" style="font-size:90px;">
						<template v-for="(line, index) in heroPrimaryLines" :key="`primary-${index}`">
							{{ line }}<br />
						</template>
					</span>
					<span v-if="heroSecondaryLines.length" class="block mt-2 text-3xl font-bold md:text-4xl">
						<template v-for="(line, index) in heroSecondaryLines" :key="`secondary-${index}`">
							{{ line }}<br />
						</template>
					</span>
					<span v-if="heroAccentLine" class="block mt-2 text-2xl font-bold text-white md:text-3xl">{{ heroAccentLine }}</span>
				</div>
			</div>

			<!-- Unified Accreditation, Details & Related Courses Card -->
			<div class="bg-white text-[#961936] shadow flex-1 px-6 pt-6 pb-[5px] grid grid-cols-1 md:grid-cols-12 gap-6 items-stretch">
				<!-- Left: Accreditation & Certificate -->
				<div class="flex flex-col items-center md:items-start border-b md:border-b-0 md:border-r border-[#961936] pb-6 md:pb-0 md:pr-6 col-span-1 md:col-span-3">
					<div class="mb-2 text-lg font-bold text-center text-black md:text-left" style="font-family:'Nortune', sans-serif;">Accredited By</div>
					<img src="/assets/img/Accreditedby.png" alt="ACTD Logo" class="w-32 mb-2" style="height: 120px;"/>
					<div class="mb-2 font-semibold text-center text-black md:text-left" style="font-family:'Nortune', sans-serif;">Certification</div>
					<img src="/assets/img/certificate_single_course.jpg" alt="Certificate" class="w-32 mb-2 rounded shadow" style="height: 200px;" />
				</div>
				<!-- Right: Details Table -->
				<div class="flex flex-col w-full h-full col-span-1 md:col-span-9">
					<div class="mb-2 text-lg font-bold text-left text-black md:text-left" style="font-family:'Nortune', sans-serif;">The Details</div>
					<table class="w-full text-sm border border-[#961936] rounded overflow-hidden mb-4">
						<thead>
							<tr class="bg-[#f5f5f5] text-black" style="font-family:'Nortune', sans-serif;">
								<th class="p-2">Where</th>
								<th class="p-2">Runs</th>
								<th class="p-2">City</th>
								<th class="p-2">Start Price</th>
								<th class="p-2"></th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="(row, i) in details"
								:key="i"
								class="border-t border-gray-200"
								:class="i % 2 === 0 ? 'text-[#961936]' : 'text-black'"
							>
								<td class="p-2">{{ row.where }}</td>
								<td class="p-2">{{ row.runs }}</td>
								<td class="p-2">{{ row.city }}</td>
								<td class="p-2 font-semibold">${{ row.price }}</td>
								<td class="p-2">
									<button class="bg-[#961936] text-white px-3 py-1 rounded hover:bg-[#a02a4a] transition" style="font-family:'Nortune', sans-serif;">Apply</button>
								</td>
							</tr>
						</tbody>
					</table>
					<!-- Related Courses Section -->
					<div class="mb-2 text-xl font-bold text-center text-black md:text-left" style="font-family:'Nortune', sans-serif;">RELATED COURSES</div>
					<div class="flex flex-col items-center flex-1 min-h-0 md:items-start">
						<div class="relative flex-1 min-h-[200px] w-[calc(100%+24px)] mr-[-24px] group">
							<button @click="prevCourse" class="absolute left-2 top-1/2 transform -translate-y-1/2 bg-[#961936] text-white rounded-full w-8 h-8 flex items-center justify-center z-10 shadow hover:bg-[#a02a4a] transition">&#8592;</button>
							<button @click="nextCourse" class="absolute right-2 top-1/2 transform -translate-y-1/2 bg-[#961936] text-white rounded-full w-8 h-8 flex items-center justify-center z-10 shadow hover:bg-[#a02a4a] transition">&#8594;</button>
							<div 
								class="absolute inset-0 flex flex-col items-center justify-center w-full h-full transition-all duration-300 rounded shadow group"
								:style="`background-image: url('${courses[currentCourse].image}'); background-size: cover; background-position: center;`"
							>
								<span class="px-4 py-2 mb-2 text-xl font-semibold text-white transition-all duration-300 bg-black rounded opacity-0 group-hover:opacity-100 bg-opacity-60">{{ courses[currentCourse].title }}</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()

const fallbackTitle = 'Health Safety Environment Management & Security Faculty'

function splitTitleIntoTokens(title) {
	const words = String(title || '')
		.trim()
		.split(/\s+/)
		.filter(Boolean)

	if (!words.length) {
		return ['Health', 'Safety', 'Environment', 'Management', '& Security', 'Faculty']
	}

	const tokens = []

	for (let index = 0; index < words.length; index += 1) {
		if (words[index] === '&' && index + 1 < words.length) {
			tokens.push(`& ${words[index + 1]}`)
			index += 1
			continue
		}

		tokens.push(words[index])
	}

	return tokens
}

const courseTitle = computed(() => route.query.title || fallbackTitle)
const titleTokens = computed(() => splitTitleIntoTokens(courseTitle.value))
const heroPrimaryLines = computed(() => titleTokens.value.slice(0, 2))
const heroSecondaryLines = computed(() => titleTokens.value.slice(2, 4))
const heroAccentLine = computed(() => titleTokens.value.slice(4).join(' '))

const details = [
	{ where: 'Tanzania', runs: 'Monthly', city: 'Arusha', price: '1895' },
	{ where: 'Uganda', runs: 'Monthly', city: 'Kampala', price: '1895' },
	{ where: 'Kenya', runs: 'Monthly', city: 'Nairobi', price: '1895' },
]

const courses = [
	{
		image: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=800&q=80',
		title: 'Fire Safety & Emergency Response',
	},
	{
		image: 'https://images.unsplash.com/photo-1519125323398-675f0ddb6308?auto=format&fit=crop&w=800&q=80',
		title: 'Workplace Hazard Management',
	},
	{
		image: 'https://images.unsplash.com/photo-1519125323398-675f0ddb6308?auto=format&fit=crop&w=800&q=80',
		title: 'Environmental Protection Training',
	},
]

const currentCourse = ref(0)

function prevCourse() {
	currentCourse.value = (currentCourse.value - 1 + courses.length) % courses.length
}

function nextCourse() {
	currentCourse.value = (currentCourse.value + 1) % courses.length
}
</script>

<script>
export default {
	name: 'TestPage'
}
</script>

<style scoped>
body {
	background: #fff;
}
</style>